import { useState, useEffect } from 'react'
import { apiClient } from '../services/api'
import type { UserDetailResponse } from '../types/api'

interface UserFormProps {
  user?: UserDetailResponse | null
  onSubmit: (success: boolean) => void
  onCancel: () => void
}

export default function UserForm({ user, onSubmit, onCancel }: UserFormProps) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'power',
    full_name: '',
    email: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        password: '',
        confirmPassword: '',
        role: user.role,
        full_name: user.full_name || '',
        email: user.email || '',
      })
    }
  }, [user])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.username.trim()) {
      newErrors.username = 'ユーザー名は必須です'
    } else if (formData.username.length < 3 || formData.username.length > 50) {
      newErrors.username = 'ユーザー名は3〜50文字です'
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'ユーザー名は英数字とアンダースコアのみです'
    }

    if (!user && !formData.password.trim()) {
      newErrors.password = 'パスワードは必須です'
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'パスワードは8文字以上です'
    }

    if (!user && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'パスワードが一致しません'
    }

    if (user && formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'パスワードが一致しません'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsSubmitting(true)
      setSuccessMessage(null)

      if (user) {
        // Update existing user
        const updateData: any = {
          full_name: formData.full_name || null,
          email: formData.email || null,
        }

        await apiClient.updateUser(user.id, updateData)
        setSuccessMessage('ユーザー情報を更新しました')

        // If password is provided, we would need a separate endpoint
        if (formData.password) {
          // TODO: Implement password change endpoint
          console.warn('Password change not yet implemented')
        }
      } else {
        // Create new user
        await apiClient.createUser({
          username: formData.username,
          password: formData.password,
          role: formData.role as 'admin' | 'power',
          full_name: formData.full_name || undefined,
          email: formData.email || undefined,
        })
        setSuccessMessage('新規ユーザーを作成しました')
      }

      setTimeout(() => {
        onSubmit(true)
      }, 1500)
    } catch (err: any) {
      console.error('Failed to submit form:', err)
      if (err.status === 409) {
        setErrors({ username: 'このユーザー名は既に使用されています' })
      } else {
        setErrors({ form: err.message || 'エラーが発生しました' })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">{user ? 'ユーザー編集' : '新規ユーザー作成'}</h5>
      </div>
      <div className="card-body">
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fas fa-check-circle me-2" />
            {successMessage}
          </div>
        )}

        {errors.form && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-circle me-2" />
            {errors.form}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Username */}
          <div className="mb-3">
            <label htmlFor="username" className="form-label">
              ユーザー名 <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.username ? 'is-invalid' : ''}`}
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={Boolean(user) || isSubmitting}
              placeholder="例: user_001"
            />
            {errors.username && <div className="invalid-feedback d-block">{errors.username}</div>}
          </div>

          {/* Role */}
          <div className="mb-3">
            <label htmlFor="role" className="form-label">
              ロール <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={isSubmitting}
            >
              <option value="admin">管理者（Admin）</option>
              <option value="power">パワーユーザー（Power）</option>
            </select>
            <small className="d-block text-muted mt-2">
              {formData.role === 'admin'
                ? '管理者: 全機能へのアクセス権。ユーザー管理が可能です。'
                : 'パワーユーザー: 割り当てられた運用先/利用先のみアクセス可能。'}
            </small>
          </div>

          {/* Password */}
          <div className="mb-3">
            <label htmlFor="password" className="form-label">
              パスワード {!user && <span className="text-danger">*</span>}
            </label>
            <input
              type="password"
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder={user ? '変更する場合のみ入力' : '8文字以上'}
            />
            {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
          </div>

          {/* Confirm Password */}
          {formData.password && (
            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label">
                パスワード確認 <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder="パスワードを再度入力"
              />
              {errors.confirmPassword && (
                <div className="invalid-feedback d-block">{errors.confirmPassword}</div>
              )}
            </div>
          )}

          {/* Full Name */}
          <div className="mb-3">
            <label htmlFor="full_name" className="form-label">
              フルネーム
            </label>
            <input
              type="text"
              className="form-control"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="例: 山田太郎"
              maxLength={100}
            />
          </div>

          {/* Email */}
          <div className="mb-3">
            <label htmlFor="email" className="form-label">
              メールアドレス
            </label>
            <input
              type="email"
              className="form-control"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="例: user@example.com"
              maxLength={100}
            />
          </div>

          {/* Buttons */}
          <div className="d-flex gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              )}
              {user ? 'ユーザー情報を更新' : 'ユーザーを作成'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
